import { createStore } from 'vuex'
import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://dhszlib.com:3003/api'

// 设置axios默认配置
axios.defaults.baseURL = API_BASE_URL
axios.defaults.timeout = 10000 // 10秒超时

// 并发控制配置
const CONCURRENT_LIMITS = {
  login: 5,        // 同时最多5个登录请求
  userInfo: 3,     // 同时最多3个用户信息请求
  dbUsers: 1       // 同时最多1个数据库用户查询
}

// 请求队列管理
class RequestQueue {
  constructor(limit) {
    this.limit = limit
    this.running = 0
    this.queue = []
  }

  async add(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject })
      this.process()
    })
  }

  async process() {
    if (this.running >= this.limit || this.queue.length === 0) {
      return
    }

    this.running++
    const { fn, resolve, reject } = this.queue.shift()

    try {
      const result = await fn()
      resolve(result)
    } catch (error) {
      reject(error)
    } finally {
      this.running--
      this.process()
    }
  }
}

// 创建请求队列实例
const requestQueues = {
  login: new RequestQueue(CONCURRENT_LIMITS.login),
  userInfo: new RequestQueue(CONCURRENT_LIMITS.userInfo),
  dbUsers: new RequestQueue(CONCURRENT_LIMITS.dbUsers)
}

// 请求去重管理
class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map()
  }

  async dedupe(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)
    }

    const promise = requestFn().finally(() => {
      this.pendingRequests.delete(key)
    })

    this.pendingRequests.set(key, promise)
    return promise
  }
}

const requestDeduplicator = new RequestDeduplicator()

// 缓存管理
class CacheManager {
  constructor() {
    this.cache = new Map()
    this.ttl = new Map()
  }

  set(key, value, ttlMs = 300000) { // 默认5分钟TTL
    this.cache.set(key, value)
    this.ttl.set(key, Date.now() + ttlMs)
  }

  get(key) {
    const expiry = this.ttl.get(key)
    if (!expiry || Date.now() > expiry) {
      this.cache.delete(key)
      this.ttl.delete(key)
      return null
    }
    return this.cache.get(key)
  }

  clear() {
    this.cache.clear()
    this.ttl.clear()
  }

  // 清理过期缓存
  cleanup() {
    const now = Date.now()
    for (const [key, expiry] of this.ttl.entries()) {
      if (now > expiry) {
        this.cache.delete(key)
        this.ttl.delete(key)
      }
    }
  }
}

const cacheManager = new CacheManager()

// 定期清理缓存
setInterval(() => {
  cacheManager.cleanup()
}, 60000) // 每分钟清理一次

// 重试机制
const retryRequest = async (fn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      if (i === maxRetries - 1) throw error
      
      // 指数退避
      const waitTime = delay * Math.pow(2, i)
      await new Promise(resolve => setTimeout(resolve, waitTime))
    }
  }
}

// 性能监控
class PerformanceMonitor {
  constructor() {
    this.metrics = {
      loginCount: 0,
      loginSuccessCount: 0,
      loginErrorCount: 0,
      avgLoginTime: 0,
      concurrentLogins: 0
    }
  }

  startLogin() {
    this.metrics.concurrentLogins++
    this.metrics.loginCount++
    return Date.now()
  }

  endLogin(startTime, success = true) {
    this.metrics.concurrentLogins--
    const duration = Date.now() - startTime
    
    if (success) {
      this.metrics.loginSuccessCount++
    } else {
      this.metrics.loginErrorCount++
    }

    // 计算平均登录时间
    this.metrics.avgLoginTime = 
      (this.metrics.avgLoginTime * (this.metrics.loginSuccessCount - 1) + duration) / 
      this.metrics.loginSuccessCount
  }

  getMetrics() {
    return { ...this.metrics }
  }

  reset() {
    this.metrics = {
      loginCount: 0,
      loginSuccessCount: 0,
      loginErrorCount: 0,
      avgLoginTime: 0,
      concurrentLogins: 0
    }
  }
}

const performanceMonitor = new PerformanceMonitor()

export default createStore({
  state: {
    user: null,
    token: null,
    isAuthenticated: false,
    dbUsers: null,
    userInfo: null,
    loading: {
      login: false,
      userInfo: false,
      dbUsers: false
    },
    errors: {
      login: null,
      userInfo: null,
      dbUsers: null
    },
    // 新增性能指标状态
    performance: {
      metrics: {},
      lastUpdated: null
    }
  },
  
  mutations: {
    setUser(state, user) {
      state.user = user
      state.isAuthenticated = !!user
      if (user) {
        localStorage.setItem('user', JSON.stringify(user))
      } else {
        localStorage.removeItem('user')
      }
    },
    
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo
      if (userInfo) {
        localStorage.setItem('userInfo', JSON.stringify(userInfo))
      } else {
        localStorage.removeItem('userInfo')
      }
    },
    
    setToken(state, { token, expiresAt }) {
      state.token = token
      if (token) {
        localStorage.setItem('token', token)
        localStorage.setItem('tokenExpiresAt', expiresAt.toString())
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      } else {
        localStorage.removeItem('token')
        localStorage.removeItem('tokenExpiresAt')
        delete axios.defaults.headers.common['Authorization']
      }
    },
    
    setDbUsers(state, users) {
      state.dbUsers = users
      // 缓存用户数据
      if (users) {
        cacheManager.set('dbUsers', users, 600000) // 10分钟缓存
      }
    },
    
    setLoading(state, { type, value }) {
      state.loading[type] = value
    },
    
    setError(state, { type, error }) {
      state.errors[type] = error
    },
    
    setPerformanceMetrics(state, metrics) {
      state.performance.metrics = metrics
      state.performance.lastUpdated = Date.now()
    },
    
    clearAuth(state) {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.dbUsers = null
      state.userInfo = null
      localStorage.removeItem('user')
      localStorage.removeItem('token')
      localStorage.removeItem('tokenExpiresAt')
      localStorage.removeItem('userInfo')
      delete axios.defaults.headers.common['Authorization']
      
      // 清理缓存
      cacheManager.clear()
    }
  },
  
  actions: {
    // 初始化应用
    initApp({ dispatch }) {
      dispatch('initAuth')
    },  
      
    // 初始化认证状态
    initAuth({ commit, dispatch }) {
      const token = localStorage.getItem('token')
      const user = localStorage.getItem('user')
      const userInfo = localStorage.getItem('userInfo')
      const expiresAt = localStorage.getItem('tokenExpiresAt')
      
      if (token && user && expiresAt) {
        const now = new Date().getTime()
        if (now < parseInt(expiresAt)) {
          commit('setToken', { token, expiresAt: parseInt(expiresAt) })
          commit('setUser', JSON.parse(user))
          if (userInfo) {
            commit('setUserInfo', JSON.parse(userInfo))
          }
          dispatch('checkTokenExpiration')
        } else {
          commit('clearAuth')
        }
      }
    },
    
    // 检查令牌是否过期
    checkTokenExpiration({ commit, state }) {
      const expiresAt = localStorage.getItem('tokenExpiresAt')
      if (!expiresAt || !state.token) {
        return false
      }
      
      const now = new Date().getTime()
      if (now >= parseInt(expiresAt)) {
        commit('clearAuth')
        return false
      }
      
      // 设置定时器在令牌过期时清除认证
      const timeUntilExpiry = parseInt(expiresAt) - now
      setTimeout(() => {
        commit('clearAuth')
      }, timeUntilExpiry)
      
      return true
    },
    
    // 优化后的数据库用户加载
    async loadDbUsers({ state, commit }) {
      // 检查缓存
      const cachedUsers = cacheManager.get('dbUsers')
      if (cachedUsers) {
        commit('setDbUsers', cachedUsers)
        return cachedUsers
      }
      
      if (state.dbUsers) return state.dbUsers
      
      // 使用请求去重
      return requestDeduplicator.dedupe('loadDbUsers', async () => {
        return requestQueues.dbUsers.add(async () => {
          commit('setLoading', { type: 'dbUsers', value: true })
          commit('setError', { type: 'dbUsers', error: null })
          
          try {
            const possiblePaths = [
              '/db.json',
              '/data/db.json',
              './db.json',
              `${window.location.origin}/db.json`
            ]
            
            let data = null
            
            // 并行尝试多个路径
            const fetchPromises = possiblePaths.map(async (path) => {
              try {
                console.log(`尝试加载db.json从路径: ${path}`)
                const response = await fetch(path, {
                  method: 'GET',
                  headers: {
                    'Accept': 'application/json',
                    'Cache-Control': 'no-cache'
                  },
                  signal: AbortSignal.timeout(5000) // 5秒超时
                })
                
                if (!response.ok) {
                  throw new Error(`HTTP ${response.status}`)
                }
                
                const contentType = response.headers.get('content-type')
                if (contentType && !contentType.includes('application/json')) {
                  throw new Error('Not JSON content')
                }
                
                const text = await response.text()
                
                if (text.trim().toLowerCase().startsWith('<!doctype') || 
                    text.trim().toLowerCase().startsWith('<html')) {
                  throw new Error('HTML response instead of JSON')
                }
                
                const parsedData = JSON.parse(text)
                console.log(`成功从路径 ${path} 加载db.json`)
                return parsedData
              } catch (error) {
                console.warn(`路径 ${path} 失败:`, error.message)
                throw error
              }
            })
            
            // 使用Promise.any获取第一个成功的结果
            try {
              data = await Promise.any(fetchPromises)
            } catch (aggregateError) {
              throw new Error('所有路径都无法加载db.json')
            }
            
            if (data && data.users) {
              commit('setDbUsers', data.users)
              console.log('成功加载db.json中的用户数据:', data.users.length, '个用户')
              return data.users
            } else {
              throw new Error('数据格式不正确')
            }
          } catch (error) {
            console.error('加载db.json失败:', error)
            commit('setError', { type: 'dbUsers', error: error.message })
            return []
          } finally {
            commit('setLoading', { type: 'dbUsers', value: false })
          }
        })
      })
    },
    
    // 优化后的用户查找
    async findUserByEmailOrUsername({ dispatch }, { email, username }) {
      const cacheKey = `user_${email || ''}_${username || ''}`
      const cachedUser = cacheManager.get(cacheKey)
      if (cachedUser) {
        return cachedUser
      }
      
      const users = await dispatch('loadDbUsers')
      if (!users || users.length === 0) return null
      
      let user = email ? users.find(user => user.email === email) : null
      
      if (!user && username) {
        user = users.find(user => user.username === username)
      }
      
      if (user) {
        console.log('找到用户:', user)
        cacheManager.set(cacheKey, user, 300000) // 5分钟缓存
      }
      
      return user
    },
    
    // 优化后的登录
    async login({ commit, dispatch }, credentials) {
      const loginKey = `login_${credentials.username}`
      
      return requestDeduplicator.dedupe(loginKey, async () => {
        return requestQueues.login.add(async () => {
          const startTime = performanceMonitor.startLogin()
          
          commit('setLoading', { type: 'login', value: true })
          commit('setError', { type: 'login', error: null })
          
          try {
            const result = await retryRequest(async () => {
              const response = await axios.post(`${API_BASE_URL}/login`, credentials)
              return response.data
            }, 2, 1000) // 最多重试2次
            
            const { token, user } = result
            
            const expiresAt = credentials.rememberMe 
              ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).getTime()
              : new Date(Date.now() + 24 * 60 * 60 * 1000).getTime()
            
            commit('setToken', { token, expiresAt })
            commit('setUser', user)
            
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
            
            dispatch('checkTokenExpiration')
            
            // 异步获取用户信息，不阻塞登录响应
            dispatch('fetchUserInfo').catch(error => {
              console.warn('获取用户信息失败:', error)
            })
            
            performanceMonitor.endLogin(startTime, true)
            commit('setPerformanceMetrics', performanceMonitor.getMetrics())
            
            return { success: true }
          } catch (error) {
            console.error('登录失败:', error)
            const errorMessage = error.response?.data?.message || '登录失败'
            commit('setError', { type: 'login', error: errorMessage })
            
            performanceMonitor.endLogin(startTime, false)
            commit('setPerformanceMetrics', performanceMonitor.getMetrics())
            
            throw new Error(errorMessage)
          } finally {
            commit('setLoading', { type: 'login', value: false })
          }
        })
      })
    },
    
    // 优化后的注册
    async register({ commit }, userData) {
      return requestQueues.login.add(async () => {
        try {
          const result = await retryRequest(async () => {
            const response = await axios.post(`${API_BASE_URL}/register`, userData)
            return response.data
          }, 2, 1000)
          
          const { token, user } = result
          
          const expiresAt = new Date(Date.now() + 2 * 60 * 60 * 1000).getTime()
          
          commit('setToken', { token, expiresAt })
          commit('setUser', user)
          
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
          
          return { success: true }
        } catch (error) {
          console.error('注册失败:', error)
          throw new Error(error.response?.data?.message || '注册失败')
        }
      })
    },
    
    async logout({ commit }) {
      try {
        await axios.post(`${API_BASE_URL}/logout`)
      } catch (error) {
        console.error('退出登录请求失败:', error)
      } finally {
        commit('clearAuth')
      }
    },
    
    async changePassword({ state }, { currentPassword, newPassword }) {
      try {
        if (!state.token) {
          throw new Error('未登录')
        }
        
        const result = await retryRequest(async () => {
          const response = await axios.post(`${API_BASE_URL}/change-password`, {
            currentPassword,
            newPassword
          }, {
            headers: {
              Authorization: `Bearer ${state.token}`
            }
          })
          return response.data
        }, 2, 1000)
        
        return result
      } catch (error) {
        console.error('修改密码失败:', error)
        throw new Error(error.response?.data?.message || '修改密码失败')
      }
    },
    
    // 优化后的用户信息获取
    async fetchUserInfo({ commit, dispatch, state }) {
      const userInfoKey = `userInfo_${state.user?.id || state.user?.username}`
      
      return requestDeduplicator.dedupe(userInfoKey, async () => {
        return requestQueues.userInfo.add(async () => {
          commit('setLoading', { type: 'userInfo', value: true })
          commit('setError', { type: 'userInfo', error: null })
          
          try {
            if (!dispatch('checkTokenExpiration')) {
              return null
            }
            
            const token = localStorage.getItem('token')
            if (!token) return null
            
            const currentUser = JSON.parse(localStorage.getItem('user') || 'null')
            if (!currentUser) return null
            
            let finalUserInfo = { ...currentUser }
            
            // 并行获取API和数据库信息
            const [apiResult, dbResult] = await Promise.allSettled([
              // API请求
              retryRequest(async () => {
                const response = await axios.get(`${API_BASE_URL}/user`, {
                  headers: {
                    Authorization: `Bearer ${token}`
                  },
                  timeout: 5000
                })
                return response.data
              }, 2, 1000).catch(error => {
                console.warn('从API获取用户信息失败:', error.message)
                return null
              }),
              
              // 数据库查询
              dispatch('findUserByEmailOrUsername', {
                email: finalUserInfo.email,
                username: finalUserInfo.username
              }).catch(error => {
                console.warn('从db.json获取用户信息失败:', error.message)
                return null
              })
            ])
            
            // 合并API结果
            if (apiResult.status === 'fulfilled' && apiResult.value?.user) {
              finalUserInfo = { ...finalUserInfo, ...apiResult.value.user }
            }
            
            // 合并数据库结果
            if (dbResult.status === 'fulfilled' && dbResult.value?.yhm) {
              finalUserInfo.yhm = dbResult.value.yhm
              console.log('从db.json获取到yhm:', dbResult.value.yhm)
            }
            
            if (!finalUserInfo.yhm && finalUserInfo.username) {
              finalUserInfo.yhm = finalUserInfo.username
            }
            
            commit('setUser', finalUserInfo)
            commit('setUserInfo', finalUserInfo)
            
            return finalUserInfo
          } catch (error) {
            console.error('获取用户信息失败:', error)
            commit('setError', { type: 'userInfo', error: error.message })
            return null
          } finally {
            commit('setLoading', { type: 'userInfo', value: false })
          }
        })
      })
    },
    
    // 新增：获取性能指标
    getPerformanceMetrics({ commit }) {
      const metrics = performanceMonitor.getMetrics()
      commit('setPerformanceMetrics', metrics)
      return metrics
    },
    
    // 新增：重置性能指标
    resetPerformanceMetrics({ commit }) {
      performanceMonitor.reset()
      commit('setPerformanceMetrics', performanceMonitor.getMetrics())
    }
  },
  
  getters: {
    isAuthenticated: state => state.isAuthenticated,
    currentUser: state => state.user,
    userInfo: state => state.userInfo,
    userDisplayName: state => {
      if (!state.user) return '未知用户'
      
      if (state.user.yhm) return state.user.yhm
      if (state.user.username) return state.user.username
      if (state.user.email) {
        const emailPrefix = state.user.email.split('@')[0]
        return emailPrefix || '未知用户'
      }
      return '未知用户'
    },
    isLoading: state => state.loading,
    errors: state => state.errors,
    performanceMetrics: state => state.performance.metrics,
    // 新增：并发状态
    concurrentStatus:  ()  => ({
      loginQueue: requestQueues.login.running,
      userInfoQueue: requestQueues.userInfo.running,
      dbUsersQueue: requestQueues.dbUsers.running
    })
  }
})